/**
 * LocationURLGenerator.ts
 * 
 * Genera URLs de verificación manual para coordenadas UTM30 (EPSG:25830),
 * permitiendo confirmar ubicaciones en Google Maps, OpenStreetMap y visor DERA.
 * 
 * @version 1.0.0
 * @since 2025-12-28
 */

import proj4 from 'proj4'

// Registrar proyecciones si no están definidas
if (!proj4.defs('EPSG:25830')) {
  proj4.defs('EPSG:25830', '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs')
}
if (!proj4.defs('EPSG:4326')) {
  proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs +type=crs')
}

// ============================================================================
// TIPOS
// ============================================================================

export interface LocationParams {
  /** Coordenada X en UTM30 ETRS89 (EPSG:25830) */
  x: number
  /** Coordenada Y en UTM30 ETRS89 (EPSG:25830) */
  y: number
  /** Nombre opcional del punto (para metadatos) */
  name?: string
  /** Municipio opcional (para metadatos) */
  municipality?: string
  /** Nivel de zoom para los visores (por defecto 17) */
  zoom?: number
}

export interface LocationURLs {
  /** URL de Google Maps centrada en la ubicación */
  googleMaps: string
  /** URL de OpenStreetMap con marcador */
  openStreetMap: string
  /** URL del visor DERA de la Junta de Andalucía */
  deraViewer: string
  /** Texto formateado lat,lon para copiar al portapapeles */
  clipboard: string
  /** Coordenadas WGS84 convertidas */
  wgs84: {
    lat: number
    lon: number
  }
}

export interface WGS84Coords {
  lat: number
  lon: number
}

// ============================================================================
// FUNCIONES PRINCIPALES
// ============================================================================

/**
 * Convierte coordenadas UTM30 (EPSG:25830) a WGS84 (lat/lon).
 * 
 * @param x - Coordenada X en metros (UTM30)
 * @param y - Coordenada Y en metros (UTM30)
 * @returns Objeto con latitud y longitud en grados decimales
 * @throws Error si la conversión falla o los valores no son finitos
 */
export function utm30ToWGS84(x: number, y: number): WGS84Coords {
  if (!isFinite(x) || !isFinite(y)) {
    throw new Error(`Coordenadas inválidas: x=${x}, y=${y}`)
  }

  const [lon, lat] = proj4('EPSG:25830', 'EPSG:4326', [x, y])

  if (!isFinite(lon) || !isFinite(lat)) {
    throw new Error(`Conversión produjo valores inválidos: lat=${lat}, lon=${lon}`)
  }

  return {
    lat: roundToDecimals(lat, 6),
    lon: roundToDecimals(lon, 6)
  }
}

/**
 * Genera URLs de verificación para múltiples plataformas.
 * 
 * @param params - Parámetros con coordenadas UTM30 y metadatos opcionales
 * @returns Objeto con URLs para Google Maps, OSM, DERA y formato clipboard
 */
export function generateLocationURLs(params: LocationParams): LocationURLs {
  const { x, y, zoom = 17 } = params

  const { lat, lon } = utm30ToWGS84(x, y)

  return {
    googleMaps: generateGoogleMapsURL(lat, lon, zoom),
    openStreetMap: generateOSMURL(lat, lon, zoom),
    deraViewer: generateDERAURL(x, y),
    clipboard: `${lat}, ${lon}`,
    wgs84: { lat, lon }
  }
}

// ============================================================================
// FUNCIONES HELPER (generadores específicos)
// ============================================================================

/**
 * Genera URL de Google Maps con marcador en la ubicación.
 * Formato: https://www.google.com/maps?q=LAT,LON&z=ZOOM
 */
function generateGoogleMapsURL(lat: number, lon: number, zoom: number = 17): string {
  return `https://www.google.com/maps?q=${lat},${lon}&z=${zoom}`
}

/**
 * Genera URL de OpenStreetMap con marcador y zoom.
 * Formato: https://www.openstreetmap.org/?mlat=LAT&mlon=LON&zoom=ZOOM
 */
function generateOSMURL(lat: number, lon: number, zoom: number = 17): string {
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}&zoom=${zoom}`
}

/**
 * Genera URL del visor DERA (IECA - Junta de Andalucía).
 *
 * P2RED-S1-3b (15-jul-2026, HX-URL-LIFECYCLE-AUDIT): el endpoint histórico
 * `…/DERA/?x=&y=` murió — redirige a la landing informativa e ignora las
 * coordenadas. El visor vigente (Mapea, publicado dic-2024) vive en
 * `…/visores/dera/`. Verificado empíricamente con Luis (captura 15-jul):
 * este visor IGNORA hoy `center`/`zoom`, pero se mantienen en la URL por si
 * IECA los habilita (coste cero, documenta la intención). Investigación del
 * mecanismo real de centrado: `OBS-URL-DERA-VIEWER-CENTER` (BACKLOG).
 */
function generateDERAURL(x: number, y: number): string {
  // Coordenadas UTM30 ETRS89 (EPSG:25830), nativas del visor
  const xRounded = Math.round(x)
  const yRounded = Math.round(y)
  return `https://www.juntadeandalucia.es/institutodeestadisticaycartografia/visores/dera/?center=${xRounded},${yRounded}&zoom=10`
}

// ============================================================================
// UTILIDADES
// ============================================================================

/**
 * Redondea un número a la cantidad especificada de decimales.
 */
function roundToDecimals(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals)
  return Math.round(value * factor) / factor
}
